import { getFormatMessage } from '@/utils/utils'
import { PlusOutlined } from '@ant-design/icons'
import {
  Button,
  Form,
  Input,
  Modal,
  Space,
  Table,
  Checkbox,
  Select
} from 'antd'
import React, { useState } from 'react'
import { UserItem, UserProps } from './interface'

export const User: React.FC<UserProps> = (props: UserProps) => {
  const [editVisible, setEditVisible] = useState<boolean>(false)
  const [form] = Form.useForm<UserItem>()
  const formatMessage = getFormatMessage()
  const formItemLayout = {
    labelCol: {
      xs: { span: 24 },
      sm: { span: 5 }
    },
    wrapperCol: {
      xs: { span: 24 },
      sm: { span: 16 }
    }
  }
  const columns = [
    {
      title: formatMessage({ id: 'app.user.table.index' }),
      key: 'app.user.table.index',
      render: (text: any, record: any, index: any) => index + 1
    },
    {
      title: formatMessage({ id: 'app.user.table.userName' }),
      key: 'app.user.table.userName',
      dataIndex: 'userName'
    },
    {
      title: formatMessage({ id: 'app.user.table.email' }),
      key: 'app.user.table.email',
      dataIndex: 'email'
    },
    {
      title: formatMessage({ id: 'app.user.table.roles' }),
      key: 'app.user.table.roles',
      render: (text: any, record: UserItem, index: any) => {
        return record.roles?.map((x) => x.name).join('|')
      }
    },
    {
      title: formatMessage({ id: 'app.user.table.operation' }),
      key: 'app.user.table.operation',
      render: (text, record: any) => (
        <Space size='middle'>
          <a
            onClick={() => {
              handleDelete(record)
            }}
          >
            {formatMessage({ id: 'app.user.table.operation.delete' })}
          </a>
        </Space>
      )
    }
  ]

  const rowSelection = {
    onChange: (selectedRowKeys: React.Key[], selectedRows: any[]) => {
      if (props.onSelect) {
        props.onSelect(selectedRows)
      }
    }
  }

  const handleDelete = (item: UserItem) => {
    Modal.confirm({
      title: formatMessage({ id: 'app.modal.confirm.delete' }),
      content: formatMessage({ id: 'app.user.confirm.delete' }),
      okText: formatMessage({ id: 'app.modal.ok' }),
      cancelText: formatMessage({ id: 'app.modal.cancel' }),
      onOk() {
        if (props.onDelete) {
          props.onDelete(item)
        }
      }
    })
  }

  const handleEditModalOk = () => {
    form
      .validateFields()
      .then((value: any) => {
        const role = props.roles?.find((x) => x.id === value.roleId)
        const merge = value
        merge.emailConfirmed = true
        merge.name = merge.surname = merge.userName
        merge.roles = [role]
        if (props.onCreate) {
          props.onCreate(merge)
        }
        form.resetFields()
        setEditVisible(false)
      })
      .catch((err) => {
        console.log(err)
      })
  }

  return (
    <div>
      <div style={{ flex: 1, textAlign: 'right', marginBottom: '16px' }}>
        <Space>
          <Button
            type='primary'
            icon={<PlusOutlined />}
            onClick={() => setEditVisible(true)}
          >
            {formatMessage({ id: 'app.user.toolbar.create' })}
          </Button>
        </Space>
      </div>
      <Table
        rowKey='id'
        columns={columns}
        dataSource={props.data}
        rowSelection={
          props.selectable
            ? {
                type: props.selectionType,
                ...rowSelection
              }
            : undefined
        }
        pagination={props.pagination}
      />
      <Modal
        destroyOnClose={true}
        maskClosable={false}
        title={formatMessage({ id: 'app.user.toolbar.create' })}
        visible={editVisible}
        onOk={handleEditModalOk}
        onCancel={() => setEditVisible(false)}
        width={500}
      >
        <Form {...formItemLayout} form={form}>
          <Form.Item
            label={formatMessage({ id: 'app.user.table.userName' })}
            name='userName'
            rules={[
              {
                required: true,
                pattern: new RegExp(/^[0-9a-zA-Z_]{1,}$/g),
                message: formatMessage({
                  id: 'app.user.table.userName.message'
                })
              }
            ]}
          >
            <Input
              placeholder={formatMessage({
                id: 'app.user.table.userName.placeholder'
              })}
            />
          </Form.Item>
          <Form.Item
            label={formatMessage({ id: 'app.user.table.password' })}
            name='password'
            rules={[
              {
                required: true,
                pattern: new RegExp(/\d+/g),
                message: formatMessage({
                  id: 'app.user.table.password.message.number'
                })
              },
              {
                required: true,
                pattern: new RegExp(/[A-Z]+/g),
                message: formatMessage({
                  id: 'app.user.table.password.message.uppercase'
                })
              },
              {
                required: true,
                pattern: new RegExp(/[a-z]+/g),
                message: formatMessage({
                  id: 'app.user.table.password.message.lowercase'
                })
              },
              {
                required: true,
                pattern: new RegExp(/[^\w\s]+/g),
                message: formatMessage({
                  id: 'app.user.table.password.message.special'
                })
              }
            ]}
          >
            <Input.Password
              placeholder={formatMessage({
                id: 'app.user.table.password.placeholder'
              })}
            />
          </Form.Item>
          <Form.Item
            label={formatMessage({ id: 'app.user.table.email' })}
            name='email'
            rules={[
              {
                required: true,
                pattern: new RegExp(
                  /^\w+([-+.]\w+)*@\w+([-.]\w+)*\.\w+([-.]\w+)*$/g
                ),
                message: formatMessage({ id: 'app.user.table.email.message' })
              }
            ]}
          >
            <Input
              placeholder={formatMessage({
                id: 'app.user.table.email.placeholder'
              })}
            />
          </Form.Item>
          <Form.Item
            label={formatMessage({ id: 'app.user.table.roles' })}
            name='roleId'
            rules={[
              {
                required: true,
                message: formatMessage({ id: 'app.user.table.roles.message' })
              }
            ]}
          >
            <Select
              placeholder={formatMessage({
                id: 'app.user.table.roles.placeholder'
              })}
            >
              {props.roles?.map((x) => (
                <Select.Option key={x.id} value={x.id || ''}>
                  {x.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            label={formatMessage({ id: 'app.user.table.lockoutEnabled' })}
            name='lockoutEnabled'
            valuePropName='checked'
          >
            <Checkbox />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}
